import { useState } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import {
  Home, BookOpen, MessageCircle, NotebookPen, Activity, TrendingUp, Wind,
  Crown, Settings, LogOut, LayoutGrid, X, MessagesSquare, Milestone, Bell, ShieldAlert, LifeBuoy, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/constants/testIds";
import NotificationBell from "@/components/NotificationBell";
import MusicToggle from "@/components/MusicToggle";
import OnboardingTour from "@/components/OnboardingTour";
import TrialBanner from "@/components/TrialBanner";

// Every destination (mirrors Brotherhood's shell so nothing is unreachable).
const ALL = [
  { to: "/home", icon: Home, label: "Home", tid: NAV.home, end: true },
  { to: "/learn", icon: BookOpen, label: "University", tid: NAV.learn },
  { to: "/coach", icon: MessageCircle, label: "Coach", tid: NAV.coach },
  { to: "/journal", icon: NotebookPen, label: "Journal", tid: NAV.journal },
  { to: "/community", icon: MessagesSquare, label: "Community", tid: NAV.community },
  { to: "/plans", icon: Milestone, label: "Plans", tid: NAV.plans },
  { to: "/wellness", icon: Activity, label: "Wellness", tid: NAV.wellness },
  { to: "/progress", icon: TrendingUp, label: "Progress", tid: NAV.progress },
  { to: "/tools", icon: Wind, label: "Tools", tid: NAV.tools },
  { to: "/resources", icon: LifeBuoy, label: "Resources", tid: NAV.resources },
  { to: "/settings", icon: Settings, label: "Settings", tid: NAV.settings },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [deskMoreOpen, setDeskMoreOpen] = useState(false);

  const doSignOut = async () => { await logout(); navigate("/"); };
  const primary = ALL.slice(0, 4); // Home, Learn, Coach, Journal
  const isOwner = user?.role === "owner";
  // Desktop top bar: keep a focused primary set inline, the rest live in "More".
  const DESK_PRIMARY = ["/home", "/learn", "/coach", "/journal", "/community", "/progress"];
  const deskPrimary = ALL.filter((i) => DESK_PRIMARY.includes(i.to));
  const deskMore = ALL.filter((i) => !DESK_PRIMARY.includes(i.to));
  const deskMoreActive = deskMore.some((i) => location.pathname.startsWith(i.to));
  const moreItems = [
    ...ALL,
    { to: "/notifications", icon: Bell, label: "Alerts", tid: "nav-notifications" },
    ...(isOwner ? [{ to: "/moderation", icon: ShieldAlert, label: "Moderation", tid: "nav-moderation" }] : []),
    { to: "/paywall", icon: Crown, label: "Upgrade", tid: "nav-upgrade" },
  ];

  const topPill = ({ isActive }) =>
    `inline-flex items-center gap-1 px-2.5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-smooth ${
      isActive ? "bg-[#291B2D] text-[#FCFAF6]" : "text-[#291B2D]/70 hover:text-[#291B2D] hover:bg-[#F7F2EA]"
    }`;

  const bottomTab = ({ isActive }) =>
    `flex flex-col items-center gap-1 py-2 rounded-2xl transition-smooth ${
      isActive ? "text-[#291B2D]" : "text-[#291B2D]/50 hover:text-[#291B2D]"
    }`;

  return (
    <div className="min-h-screen bg-[#FCFAF6] overflow-x-clip">
      {/* Desktop TopNav */}
      <header data-testid="top-nav"
        className="hidden lg:block sticky top-0 z-40 bg-[#FCFAF6]/85 backdrop-blur-xl border-b border-[#291B2D]/5">
        <div className="max-w-[1500px] mx-auto px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="topnav-brand" title="Go to landing page" className="flex items-center gap-2 shrink-0">
            <span className="w-8 h-8 rounded-2xl bg-[#291B2D] text-[#FCFAF6] flex items-center justify-center font-heading text-sm">
              S<span className="text-[#C89B55]">+</span>
            </span>
            <span className="hidden xl:inline font-heading text-lg tracking-tight sh-goldshine">
              Sisterhood<span className="text-[#C89B55]">+</span>
            </span>
          </Link>
          <nav className="flex items-center gap-1 min-w-0 flex-1 justify-center">
            {deskPrimary.map(({ to, icon: Icon, label, tid, end }) => (
              <NavLink key={to} to={to} end={end} data-testid={tid} title={label} className={topPill}>
                <Icon size={15} strokeWidth={1.75} />
                <span className="hidden xl:inline">{label}</span>
              </NavLink>
            ))}
            <div className="relative">
              <button
                data-testid="nav-more"
                title="More"
                onClick={() => setDeskMoreOpen((o) => !o)}
                className={topPill({ isActive: deskMoreActive })}
              >
                <LayoutGrid size={15} strokeWidth={1.75} />
                <span className="hidden xl:inline">More</span>
                <ChevronDown size={13} className={`transition-transform ${deskMoreOpen ? "rotate-180" : ""}`} />
              </button>
              {deskMoreOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setDeskMoreOpen(false)} />
                  <div data-testid="nav-more-menu"
                    className="absolute right-0 mt-2 z-50 w-56 bg-white rounded-2xl shadow-xl ring-1 ring-[#291B2D]/5 p-2 animate-fade-up">
                    {deskMore.map(({ to, icon: Icon, label, tid }) => (
                      <NavLink key={to} to={to} data-testid={`more-${tid}`} onClick={() => setDeskMoreOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-smooth ${
                            isActive ? "bg-[#291B2D] text-[#FCFAF6]" : "text-[#291B2D]/75 hover:bg-[#F7F2EA]"
                          }`}>
                        <Icon size={16} strokeWidth={1.75} /> {label}
                      </NavLink>
                    ))}
                  </div>
                </>
              )}
            </div>
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            {isOwner && (
              <NavLink to="/moderation" data-testid="nav-moderation" title="Moderation"
                className={({ isActive }) => `inline-flex items-center justify-center h-10 w-10 rounded-full border transition-smooth ${isActive ? "bg-[#291B2D] text-[#FCFAF6] border-[#291B2D]" : "border-[#291B2D]/10 bg-white text-[#291B2D]/70 hover:text-[#291B2D] hover:bg-[#F7F2EA]"}`}>
                <ShieldAlert size={16} strokeWidth={1.75} />
              </NavLink>
            )}
            <MusicToggle />
            <NotificationBell />
            {!user?.is_premium && (
              <NavLink to="/paywall" data-testid={NAV.upgrade}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#C89B55] text-[#291B2D] px-4 py-2 text-sm font-semibold hover:bg-[#B38743] transition-smooth">
                <Crown size={14} /> Upgrade
              </NavLink>
            )}
            <button data-testid={NAV.logout} onClick={doSignOut} aria-label="Sign out"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#291B2D]/10 bg-white text-[#291B2D]/70 hover:text-[#9E3A3A] hover:border-[#9E3A3A]/20 hover:bg-[#FAF0F0] transition-smooth">
              <LogOut size={14} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-md lg:max-w-6xl mx-auto pb-28 lg:pb-16 px-4 sm:px-6 lg:px-10 pt-5 lg:pt-8 overflow-x-clip">
        <TrialBanner />
        {children}
        <OnboardingTour />
        <footer className="mt-16 pt-8 border-t border-[#291B2D]/5 dark:border-white/10 text-center text-[11px] text-[#291B2D]/45 dark:text-white/35 space-y-1" data-testid="app-footer">
          <p>Sisterhood+ is not a substitute for professional care · In crisis, call or text <a href="tel:988" className="underline">988</a></p>
          <p className="text-center font-medium">© 2026 Renewing Minds Counseling and Consulting Services. All rights reserved.</p>
        </footer>
      </div>

      {/* Mobile "More" sheet */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end"
          onClick={() => setMoreOpen(false)} data-testid="bottom-nav-more-sheet">
          <div className="w-full bg-white rounded-t-3xl p-5 pb-[calc(2rem_+_env(safe-area-inset-bottom))] animate-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-heading text-xl">All sections</p>
              <button onClick={() => setMoreOpen(false)} aria-label="Close menu"
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#291B2D]/50 hover:bg-[#F7F2EA]">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {moreItems.map(({ to, icon: Icon, label, tid }) => (
                <NavLink key={to} to={to} data-testid={`m-${tid}`} onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-2 py-4 px-1 rounded-2xl transition-smooth ${
                      isActive ? "bg-[#291B2D] text-[#FCFAF6]" : "bg-[#F7F2EA]/60 text-[#291B2D]/75 hover:bg-[#F7F2EA]"
                    }`}>
                  <Icon size={22} strokeWidth={1.75} />
                  <span className="text-[11px] font-medium text-center leading-tight">{label}</span>
                </NavLink>
              ))}
              <button onClick={doSignOut} data-testid="m-nav-logout"
                className="flex flex-col items-center justify-center gap-2 py-4 px-1 rounded-2xl bg-[#F7F2EA]/60 text-[#291B2D]/75 hover:bg-[#F7F2EA]">
                <LogOut size={22} strokeWidth={1.75} />
                <span className="text-[11px] font-medium text-center leading-tight">Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global music control (mobile floating) */}
      <MusicToggle variant="floating" />

      {/* Mobile BottomNav */}
      <nav data-testid="bottom-nav"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-[#291B2D]/5 pb-safe">
        <div className="max-w-md mx-auto grid grid-cols-5 gap-0.5 px-1 py-2">
          {primary.map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`bottomnav-${label.toLowerCase()}`} className={bottomTab}>
              {({ isActive }) => (<>
                <Icon size={22} strokeWidth={isActive ? 2 : 1.5} />
                <span className="text-[11px] font-medium tracking-wide">{label}</span>
              </>)}
            </NavLink>
          ))}
          <button onClick={() => setMoreOpen(true)} data-testid={NAV.mobileMenu} aria-label="More sections"
            className="flex flex-col items-center gap-1 py-2 rounded-2xl transition-smooth text-[#291B2D]/50 hover:text-[#291B2D]">
            <LayoutGrid size={22} strokeWidth={1.5} />
            <span className="text-[11px] font-medium tracking-wide">More</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
